import { useEffect, useMemo, useState } from "react";
import {
  ArrowDownUp,
  BarChart3,
  BriefcaseBusiness,
  CheckCircle2,
  CircleDollarSign,
  Download,
  Gauge,
  LogIn,
  LogOut,
  Moon,
  RefreshCw,
  Scale,
  Search,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Sun,
  Table2,
  TriangleAlert,
  UserRound
} from "lucide-react";
import { demoPortfolio, goldZoneSettings } from "./data/demoData";
import { classifyGoldZone, currentRatio, debtToEquity, freeCashFlowMargin, netDebt, netMargin, operatingMargin, payoutRatio, quickRatio, returnVersusBenchmark, roe, roic } from "./lib/calculations";
import { formatCurrency, formatNumber, formatPercent, statusLabel } from "./lib/format";
import { isSupabaseConfigured, supabase } from "./lib/supabase";
import { warningsForRecord } from "./lib/warnings";
import { loadInvestments } from "./services/dataService";
import type { GoldZoneSettings, InvestmentRecord, PortfolioPosition, Recommendation, Zone } from "./types/domain";

type Page = "dashboard" | "gold" | "compare" | "portfolio" | "admin";
type SortKey = "rank" | "ticker" | "buffett" | "opportunity" | "margin" | "cagr";

const navItems: Array<{ page: Page; label: string; icon: typeof Gauge }> = [
  { page: "dashboard", label: "Dashboard", icon: Gauge },
  { page: "gold", label: "Gold Zone", icon: ShieldCheck },
  { page: "compare", label: "Compare", icon: Scale },
  { page: "portfolio", label: "Portfolio", icon: BriefcaseBusiness },
  { page: "admin", label: "Admin", icon: Settings }
];

const zoneClasses: Record<Zone, string> = {
  Gold: "border-yellow-400/40 bg-yellow-400/15 text-yellow-200 light:text-yellow-800",
  Silver: "border-slate-300/40 bg-slate-300/15 text-slate-100 light:text-slate-700",
  Watch: "border-blue-400/40 bg-blue-400/15 text-blue-200 light:text-blue-800",
  "Fair Value": "border-emerald-400/40 bg-emerald-400/15 text-emerald-200 light:text-emerald-800",
  Avoid: "border-red-400/40 bg-red-400/15 text-red-200 light:text-red-800"
};

const recommendationClasses: Record<Recommendation, string> = {
  "Strong Buy": "text-yellow-300 light:text-yellow-700",
  Buy: "text-emerald-300 light:text-emerald-700",
  Hold: "text-blue-300 light:text-blue-700",
  Wait: "text-slate-300 light:text-slate-700",
  Reduce: "text-orange-300 light:text-orange-700",
  Avoid: "text-red-300 light:text-red-700"
};

const savingsBenchmark = 0.11;

function App() {
  const [darkMode, setDarkMode] = useState(true);
  const [page, setPage] = useState<Page>("dashboard");
  const [records, setRecords] = useState<InvestmentRecord[]>([]);
  const [dataMessage, setDataMessage] = useState("Loading GBVI data...");
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    query: "",
    market: "All",
    zone: "All",
    recommendation: "All",
    sector: "All",
    minBuffett: 0,
    minOpportunity: 0,
    minMargin: 0,
    minCagr: 0,
    dividendOnly: false
  });
  const [sortKey, setSortKey] = useState<SortKey>("opportunity");
  const [settings, setSettings] = useState<GoldZoneSettings>(goldZoneSettings);
  const [portfolio, setPortfolio] = useState<PortfolioPosition[]>(demoPortfolio);

  useEffect(() => {
    document.documentElement.classList.toggle("light", !darkMode);
    document.documentElement.classList.toggle("dark", darkMode);
  }, [darkMode]);

  useEffect(() => {
    void loadInvestments().then((result) => {
      setRecords(result.records);
      setDataMessage(result.message);
    });
  }, []);

  const rankedRecords = useMemo(
    () =>
      records
        .map((record) => ({
          ...record,
          calculatedZone: classifyGoldZone(
            {
              buffettScore: record.quality.buffettScore,
              opportunityScore: record.quality.opportunityScore,
              marginOfSafety: record.valuation.marginOfSafety,
              expectedCagr: record.valuation.expectedFiveYearCagr,
              normalizedFreeCashFlow: record.financials[0]?.freeCashFlow ?? null,
              unresolvedCriticalWarning: record.recommendation.unresolvedCriticalWarning,
              acceptableDebtStatus: (debtToEquity(record.financials[0]?.totalDebt ?? null, record.financials[0]?.shareholdersEquity ?? null).value ?? 10) < 2.5
            },
            settings
          )
        }))
        .sort((a, b) => b.quality.opportunityScore - a.quality.opportunityScore),
    [records, settings]
  );

  const sectors = Array.from(new Set(records.map((record) => record.company.sector))).sort();

  const filteredRecords = useMemo(() => {
    const filtered = rankedRecords.filter((record) => {
      const query = filters.query.toLowerCase();
      return (
        (!query || `${record.company.ticker} ${record.company.companyName}`.toLowerCase().includes(query)) &&
        (filters.market === "All" || record.company.market === filters.market) &&
        (filters.zone === "All" || record.calculatedZone === filters.zone) &&
        (filters.recommendation === "All" || record.recommendation.recommendation === filters.recommendation) &&
        (filters.sector === "All" || record.company.sector === filters.sector) &&
        record.quality.buffettScore >= filters.minBuffett &&
        record.quality.opportunityScore >= filters.minOpportunity &&
        (record.valuation.marginOfSafety ?? -999) >= filters.minMargin / 100 &&
        (record.valuation.expectedFiveYearCagr ?? -999) >= filters.minCagr / 100 &&
        (!filters.dividendOnly || (record.dividendYield ?? 0) > 0)
      );
    });

    return [...filtered].sort((a, b) => {
      switch (sortKey) {
        case "ticker":
          return a.company.ticker.localeCompare(b.company.ticker);
        case "buffett":
          return b.quality.buffettScore - a.quality.buffettScore;
        case "margin":
          return (b.valuation.marginOfSafety ?? -999) - (a.valuation.marginOfSafety ?? -999);
        case "cagr":
          return (b.valuation.expectedFiveYearCagr ?? -999) - (a.valuation.expectedFiveYearCagr ?? -999);
        case "rank":
        case "opportunity":
        default:
          return b.quality.opportunityScore - a.quality.opportunityScore;
      }
    });
  }, [filters, rankedRecords, sortKey]);

  const selectedRecord = selectedSlug ? rankedRecords.find((record) => record.company.slug === selectedSlug) ?? null : null;

  const metrics = useMemo(() => {
    const total = rankedRecords.length;
    const byZone = (zone: Zone) => rankedRecords.filter((record) => record.calculatedZone === zone).length;
    const average = (selector: (record: InvestmentRecord) => number) => (total ? rankedRecords.reduce((sum, record) => sum + selector(record), 0) / total : 0);
    const priceDates = rankedRecords.map((record) => record.price.priceDate).filter(Boolean).sort();
    const financialDates = rankedRecords.map((record) => record.financials[0]?.periodEndDate).filter(Boolean).sort();
    const latestPrice = priceDates[priceDates.length - 1] ?? "Unavailable";
    const latestFinancial = financialDates[financialDates.length - 1] ?? "Unavailable";
    return { total, gold: byZone("Gold"), silver: byZone("Silver"), fair: byZone("Fair Value"), avoid: byZone("Avoid"), buffett: average((r) => r.quality.buffettScore), opportunity: average((r) => r.quality.opportunityScore), latestPrice, latestFinancial };
  }, [rankedRecords]);

  return (
    <main className="min-h-screen bg-ink text-slate-100 transition light:bg-slate-50 light:text-slate-950">
      <header className="sticky top-0 z-20 border-b border-slate-800 bg-ink/90 backdrop-blur light:border-slate-200 light:bg-white/90">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 py-3">
          <div className="mr-auto">
            <p className="text-xs uppercase tracking-widest text-gold">GBM+ Buffett Value Index</p>
            <h1 className="text-xl font-semibold sm:text-2xl">GBVI</h1>
          </div>
          <nav className="flex max-w-full gap-1 overflow-x-auto rounded-md border border-slate-800 p-1 light:border-slate-200">
            {navItems.map((item) => (
              <button
                key={item.page}
                className={`flex h-10 items-center gap-2 rounded px-3 text-sm transition ${page === item.page ? "bg-blue text-white" : "text-slate-300 hover:bg-slate-800 light:text-slate-700 light:hover:bg-slate-100"}`}
                onClick={() => {
                  setPage(item.page);
                  setSelectedSlug(null);
                }}
              >
                <item.icon size={16} />
                {item.label}
              </button>
            ))}
          </nav>
          <button className="icon-button" onClick={() => setDarkMode((value) => !value)} aria-label="Toggle light mode">
            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>
      </header>

      <section className="mx-auto max-w-7xl px-4 py-5">
        <div className="mb-5 flex flex-wrap items-center gap-3 rounded-md border border-yellow-400/30 bg-yellow-400/10 px-4 py-3 text-sm text-yellow-100 light:text-yellow-900">
          <TriangleAlert size={18} />
          <span>Sample data only. Not live, not investment advice, and for development use until approved source records are loaded.</span>
          <span className="ml-auto text-xs text-slate-300 light:text-slate-600">{dataMessage}</span>
        </div>

        {selectedRecord ? (
          <CompanyDetail record={selectedRecord} onBack={() => setSelectedSlug(null)} />
        ) : page === "dashboard" ? (
          <Dashboard
            metrics={metrics}
            records={filteredRecords}
            allRecords={rankedRecords}
            sectors={sectors}
            filters={filters}
            setFilters={setFilters}
            sortKey={sortKey}
            setSortKey={setSortKey}
            onSelect={setSelectedSlug}
          />
        ) : page === "gold" ? (
          <GoldZone records={rankedRecords} settings={settings} setSettings={setSettings} onSelect={setSelectedSlug} />
        ) : page === "compare" ? (
          <Compare records={rankedRecords} />
        ) : page === "portfolio" ? (
          <Portfolio records={rankedRecords} portfolio={portfolio} setPortfolio={setPortfolio} />
        ) : (
          <Admin records={rankedRecords} settings={settings} setSettings={setSettings} />
        )}
      </section>
    </main>
  );
}

function Dashboard({
  metrics,
  records,
  allRecords,
  sectors,
  filters,
  setFilters,
  sortKey,
  setSortKey,
  onSelect
}: {
  metrics: Record<string, number | string>;
  records: Array<InvestmentRecord & { calculatedZone: Zone }>;
  allRecords: Array<InvestmentRecord & { calculatedZone: Zone }>;
  sectors: string[];
  filters: any;
  setFilters: (filters: any) => void;
  sortKey: SortKey;
  setSortKey: (key: SortKey) => void;
  onSelect: (slug: string) => void;
}) {
  if (!allRecords.length) return <EmptyState />;
  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-4 xl:grid-cols-8">
        <Stat label="Tracked" value={metrics.total} icon={Table2} />
        <Stat label="Gold" value={metrics.gold} icon={ShieldCheck} />
        <Stat label="Silver" value={metrics.silver} icon={Scale} />
        <Stat label="Fair" value={metrics.fair} icon={Gauge} />
        <Stat label="Avoid" value={metrics.avoid} icon={TriangleAlert} />
        <Stat label="Avg Buffett" value={formatNumber(Number(metrics.buffett), 1)} icon={BarChart3} />
        <Stat label="Avg Opportunity" value={formatNumber(Number(metrics.opportunity), 1)} icon={CircleDollarSign} />
        <Stat label="Updates" value={String(metrics.latestPrice)} icon={RefreshCw} small />
      </div>

      <FilterBar filters={filters} setFilters={setFilters} sectors={sectors} sortKey={sortKey} setSortKey={setSortKey} />
      <RankingTable records={records} onSelect={onSelect} />
      <p className="text-xs text-slate-400">Latest financial-data update: {String(metrics.latestFinancial)}. All shown figures include a visible data status.</p>
    </div>
  );
}

function Stat({ label, value, icon: Icon, small = false }: { label: string; value: string | number; icon: typeof Gauge; small?: boolean }) {
  return (
    <div className="surface rounded-md p-4">
      <div className="flex items-center justify-between text-slate-400 light:text-slate-500">
        <span className="text-xs uppercase">{label}</span>
        <Icon size={17} />
      </div>
      <div className={`mt-3 font-semibold ${small ? "text-base" : "text-2xl"}`}>{value}</div>
      <StatusPill status="sample" />
    </div>
  );
}

function FilterBar({ filters, setFilters, sectors, sortKey, setSortKey }: { filters: any; setFilters: (filters: any) => void; sectors: string[]; sortKey: SortKey; setSortKey: (key: SortKey) => void }) {
  const update = (key: string, value: string | number | boolean) => setFilters({ ...filters, [key]: value });
  return (
    <div className="surface rounded-md p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
        <SlidersHorizontal size={17} />
        Filters
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <label className="relative">
          <Search className="absolute left-3 top-2.5 text-slate-500" size={16} />
          <input className="field w-full pl-9" placeholder="Ticker or company" value={filters.query} onChange={(event) => update("query", event.target.value)} />
        </label>
        <Select value={filters.market} onChange={(value) => update("market", value)} options={["All", "BMV", "SIC", "US", "Other international"]} />
        <Select value={filters.zone} onChange={(value) => update("zone", value)} options={["All", "Gold", "Silver", "Watch", "Fair Value", "Avoid"]} />
        <Select value={filters.recommendation} onChange={(value) => update("recommendation", value)} options={["All", "Strong Buy", "Buy", "Hold", "Wait", "Reduce", "Avoid"]} />
        <Select value={filters.sector} onChange={(value) => update("sector", value)} options={["All", ...sectors]} />
        <NumberFilter label="Min Buffett" value={filters.minBuffett} onChange={(value) => update("minBuffett", value)} />
        <NumberFilter label="Min Opportunity" value={filters.minOpportunity} onChange={(value) => update("minOpportunity", value)} />
        <NumberFilter label="Min MoS %" value={filters.minMargin} onChange={(value) => update("minMargin", value)} />
        <NumberFilter label="Min CAGR %" value={filters.minCagr} onChange={(value) => update("minCagr", value)} />
        <Select value={sortKey} onChange={(value) => setSortKey(value as SortKey)} options={["opportunity", "buffett", "margin", "cagr", "ticker", "rank"]} />
      </div>
      <label className="mt-3 flex items-center gap-2 text-sm text-slate-300 light:text-slate-700">
        <input type="checkbox" checked={filters.dividendOnly} onChange={(event) => update("dividendOnly", event.target.checked)} />
        Dividend-paying only
      </label>
    </div>
  );
}

function Select({ value, onChange, options }: { value: string; onChange: (value: string) => void; options: string[] }) {
  return (
    <select className="field w-full" value={value} onChange={(event) => onChange(event.target.value)}>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  );
}

function NumberFilter({ label, value, onChange }: { label: string; value: number; onChange: (value: number) => void }) {
  return <input className="field w-full" type="number" min="0" max="100" placeholder={label} value={value || ""} onChange={(event) => onChange(Number(event.target.value || 0))} />;
}

function RankingTable({ records, onSelect }: { records: Array<InvestmentRecord & { calculatedZone: Zone }>; onSelect: (slug: string) => void }) {
  return (
    <div className="surface overflow-hidden rounded-md">
      <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3 light:border-slate-200">
        <h2 className="text-lg font-semibold">Ranked Opportunities</h2>
        <ArrowDownUp size={17} className="text-slate-400" />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1050px] text-left text-sm">
          <thead className="bg-slate-900 text-xs uppercase text-slate-400 light:bg-slate-100 light:text-slate-600">
            <tr>
              {["Rank", "Ticker", "Company", "Market", "Current", "Daily %", "MXN", "Intrinsic", "MoS", "5Y CAGR", "Dividend", "Buffett", "Opportunity", "Zone", "Recommendation", "Updated"].map((head) => (
                <th key={head} className="px-3 py-3 font-medium">{head}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800 light:divide-slate-200">
            {records.map((record, index) => (
              <tr key={record.company.id} className="hover:bg-slate-900/70 light:hover:bg-slate-100">
                <td className="px-3 py-3 text-slate-400">{index + 1}</td>
                <td className="px-3 py-3">
                  <button className="font-semibold text-blue-300 underline-offset-4 hover:underline light:text-blue-700" onClick={() => onSelect(record.company.slug)}>
                    {record.company.ticker}
                  </button>
                  <StatusPill status={record.price.dataStatus} />
                </td>
                <td className="px-3 py-3">{record.company.companyName}</td>
                <td className="px-3 py-3">{record.company.market}</td>
                <td className="px-3 py-3">{formatCurrency(record.price.price, record.price.currency)}</td>
                <td className={`px-3 py-3 font-semibold ${(record.price.dailyChangePercent ?? 0) < -0.08 ? "text-red-300 light:text-red-700" : (record.price.dailyChangePercent ?? 0) > 0 ? "text-emerald-300 light:text-emerald-700" : ""}`}>{formatPercent(record.price.dailyChangePercent)}</td>
                <td className="px-3 py-3">{formatCurrency(record.price.priceMxn, "MXN")}</td>
                <td className="px-3 py-3">{formatCurrency(record.valuation.baseValue, record.price.currency)}</td>
                <td className="px-3 py-3">{formatPercent(record.valuation.marginOfSafety)}</td>
                <td className="px-3 py-3">{formatPercent(record.valuation.expectedFiveYearCagr)}</td>
                <td className="px-3 py-3">{formatPercent(record.dividendYield)}</td>
                <td className="px-3 py-3">{record.quality.buffettScore}</td>
                <td className="px-3 py-3">{record.quality.opportunityScore}</td>
                <td className="px-3 py-3"><ZonePill zone={record.calculatedZone} /></td>
                <td className={`px-3 py-3 font-semibold ${recommendationClasses[record.recommendation.recommendation]}`}>{record.recommendation.recommendation}</td>
                <td className="px-3 py-3">{record.price.priceDate}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CompanyDetail({ record, onBack }: { record: InvestmentRecord & { calculatedZone?: Zone }; onBack: () => void }) {
  const latest = record.financials[0];
  const ratios = [
    ["ROE", roe(latest.netIncome, latest.shareholdersEquity).value],
    ["ROIC", roic(latest.operatingIncome, 0.3, latest.totalDebt, latest.shareholdersEquity, latest.cashAndEquivalents).value],
    ["Operating margin", operatingMargin(latest.operatingIncome, latest.revenue).value],
    ["Net margin", netMargin(latest.netIncome, latest.revenue).value],
    ["Debt-to-equity", debtToEquity(latest.totalDebt, latest.shareholdersEquity).value],
    ["Current ratio", currentRatio(latest.currentAssets, latest.currentLiabilities).value],
    ["Quick ratio", quickRatio(latest.currentAssets, latest.inventory, latest.currentLiabilities).value],
    ["FCF margin", freeCashFlowMargin(latest.freeCashFlow, latest.revenue).value],
    ["Payout ratio", payoutRatio(latest.dividendsPaid, latest.netIncome).value],
    ["Net debt", netDebt(latest.totalDebt, latest.cashAndEquivalents).value]
  ];

  return (
    <div className="space-y-5">
      <button className="rounded-md border border-slate-700 px-3 py-2 text-sm hover:border-blue light:border-slate-300" onClick={onBack}>Back to dashboard</button>
      <section className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="surface rounded-md p-5">
          <div className="flex flex-wrap items-start gap-3">
            <div className="mr-auto">
              <h2 className="text-3xl font-semibold">{record.company.companyName}</h2>
              <p className="mt-1 text-slate-400">{record.company.ticker} · {record.company.market} · {record.company.sector} · {record.company.industry}</p>
            </div>
            <ZonePill zone={record.calculatedZone ?? record.recommendation.zone} />
            <StatusPill status={record.recommendation.dataStatus} />
          </div>
          <p className="mt-5 text-sm leading-6 text-slate-300 light:text-slate-700">{record.company.businessDescription}</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Metric label="Current price" value={formatCurrency(record.price.price, record.price.currency)} status={record.price.dataStatus} />
            <Metric label="MXN equivalent" value={formatCurrency(record.price.priceMxn, "MXN")} status={record.price.dataStatus} />
            <Metric label="Daily change" value={formatPercent(record.price.dailyChangePercent)} status={record.price.dataStatus} />
            <Metric label="Daily volume" value={formatNumber(record.price.volume, 0)} status={record.price.dataStatus} />
            <Metric label="Base value" value={formatCurrency(record.valuation.baseValue, record.price.currency)} status={record.valuation.dataStatus} />
            <Metric label="Margin of safety" value={formatPercent(record.valuation.marginOfSafety)} status={record.valuation.dataStatus} />
            <Metric label="Expected 5Y price" value={formatCurrency(record.valuation.expectedFiveYearPrice, record.price.currency)} status={record.valuation.dataStatus} />
            <Metric label="Expected CAGR" value={formatPercent(record.valuation.expectedFiveYearCagr)} status={record.valuation.dataStatus} />
            <Metric label="Dividend yield" value={formatPercent(record.dividendYield)} status={record.quality.dataStatus} />
            <Metric label="Dividend safety" value={record.dividendSafety} status={record.quality.dataStatus} />
          </div>
        </div>
        <div className="surface rounded-md p-5">
          <h3 className="font-semibold">Valuation Bands</h3>
          <div className="mt-4 space-y-3 text-sm">
            <Band label="Conservative" value={record.valuation.conservativeValue} currency={record.price.currency} />
            <Band label="Strong buy" value={record.valuation.strongBuyPrice} currency={record.price.currency} />
            <Band label="Buy" value={record.valuation.buyPrice} currency={record.price.currency} />
            <Band label="Fair low" value={record.valuation.fairValueLow} currency={record.price.currency} />
            <Band label="Fair high" value={record.valuation.fairValueHigh} currency={record.price.currency} />
            <Band label="Take profit" value={record.valuation.takeProfitPrice} currency={record.price.currency} />
          </div>
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        <TextPanel title="Qualitative Analysis" lines={[record.quality.moatNotes, record.quality.managementNotes, record.quality.riskNotes, record.recommendation.investmentThesis]} />
        <TextPanel title="Reasons To Buy" lines={record.recommendation.reasonsToBuy} />
        <TextPanel title="Reasons Not To Buy" lines={record.recommendation.reasonsNotToBuy} />
      </section>

      <section className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
        <FinancialTable record={record} />
        <div className="surface rounded-md p-5">
          <h3 className="font-semibold">Ratios</h3>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {ratios.map(([label, value]) => (
              <Metric key={label as string} label={label as string} value={typeof value === "number" && String(label).includes("debt") ? formatNumber(value) : formatPercent(value as number | null)} status={latest.dataStatus} />
            ))}
          </div>
          <h3 className="mt-6 font-semibold">Warnings</h3>
          <ul className="mt-3 space-y-2 text-sm text-yellow-100 light:text-yellow-900">
            {warningsForRecord(record).map((warning) => <li key={warning}>{warning}</li>)}
          </ul>
        </div>
      </section>
    </div>
  );
}

function Metric({ label, value, status }: { label: string; value: string | number; status: "approved" | "pending" | "sample" | "stale" | "unavailable" }) {
  return (
    <div className="rounded-md border border-slate-800 p-3 light:border-slate-200">
      <div className="text-xs uppercase text-slate-500">{label}</div>
      <div className="mt-1 text-lg font-semibold">{value}</div>
      <StatusPill status={status} />
    </div>
  );
}

function Band({ label, value, currency }: { label: string; value: number | null; currency: string }) {
  return (
    <div>
      <div className="mb-1 flex justify-between"><span>{label}</span><span>{formatCurrency(value, currency)}</span></div>
      <div className="h-2 rounded bg-slate-800 light:bg-slate-200"><div className="h-2 rounded bg-gradient-to-r from-blue to-gold" style={{ width: `${Math.min(100, Math.max(10, ((value ?? 0) / 600) * 100))}%` }} /></div>
    </div>
  );
}

function FinancialTable({ record }: { record: InvestmentRecord }) {
  const rows = ["revenue", "netIncome", "eps", "operatingCashFlow", "capitalExpenditure", "freeCashFlow", "cashAndEquivalents", "totalDebt", "shareholdersEquity", "dividendsPaid"] as const;
  return (
    <div className="surface overflow-hidden rounded-md">
      <h3 className="border-b border-slate-800 px-5 py-4 font-semibold light:border-slate-200">Historical Financials</h3>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-slate-900 text-xs uppercase text-slate-400 light:bg-slate-100">
            <tr><th className="px-4 py-3 text-left">Metric</th>{record.financials.map((period) => <th className="px-4 py-3 text-right" key={period.fiscalYear}>{period.fiscalYear}</th>)}<th className="px-4 py-3 text-right">Status</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-800 light:divide-slate-200">
            {rows.map((row) => (
              <tr key={row}>
                <td className="px-4 py-3 capitalize">{row.replace(/([A-Z])/g, " $1")}</td>
                {record.financials.map((period) => <td className="px-4 py-3 text-right" key={`${row}-${period.fiscalYear}`}>{formatNumber(period[row], 2)}</td>)}
                <td className="px-4 py-3 text-right"><StatusPill status="sample" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GoldZone({ records, settings, setSettings, onSelect }: { records: Array<InvestmentRecord & { calculatedZone: Zone }>; settings: GoldZoneSettings; setSettings: (settings: GoldZoneSettings) => void; onSelect: (slug: string) => void }) {
  const gold = records.filter((record) => record.calculatedZone === "Gold");
  return (
    <div className="space-y-5">
      <div className="surface rounded-md p-5">
        <h2 className="text-2xl font-semibold">Gold Zone Rules</h2>
        <div className="mt-4 grid gap-3 md:grid-cols-4">
          <NumberSetting label="Buffett score" value={settings.minimumBuffettScore} onChange={(value) => setSettings({ ...settings, minimumBuffettScore: value })} />
          <NumberSetting label="Opportunity score" value={settings.minimumOpportunityScore} onChange={(value) => setSettings({ ...settings, minimumOpportunityScore: value })} />
          <NumberSetting label="Margin of safety %" value={settings.minimumMarginOfSafety * 100} onChange={(value) => setSettings({ ...settings, minimumMarginOfSafety: value / 100 })} />
          <NumberSetting label="Expected CAGR %" value={settings.minimumExpectedCagr * 100} onChange={(value) => setSettings({ ...settings, minimumExpectedCagr: value / 100 })} />
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-sm">
          <label><input type="checkbox" checked={settings.requirePositiveFcf} onChange={(event) => setSettings({ ...settings, requirePositiveFcf: event.target.checked })} /> Positive FCF required</label>
          <label><input type="checkbox" checked={settings.requireNoCriticalWarning} onChange={(event) => setSettings({ ...settings, requireNoCriticalWarning: event.target.checked })} /> No critical warning required</label>
        </div>
      </div>
      <RankingTable records={gold} onSelect={onSelect} />
    </div>
  );
}

function NumberSetting({ label, value, onChange }: { label: string; value: number; onChange: (value: number) => void }) {
  return (
    <label className="text-sm text-slate-300 light:text-slate-700">
      <span className="mb-1 block">{label}</span>
      <input className="field w-full" type="number" value={value} onChange={(event) => onChange(Number(event.target.value))} />
    </label>
  );
}

function Compare({ records }: { records: Array<InvestmentRecord & { calculatedZone: Zone }> }) {
  const [selected, setSelected] = useState(records.slice(0, 4).map((record) => record.company.slug));
  const selectedRecords = selected.map((slug) => records.find((record) => record.company.slug === slug)).filter(Boolean) as Array<InvestmentRecord & { calculatedZone: Zone }>;
  const rows = [
    ["Current price", (r: InvestmentRecord) => formatCurrency(r.price.price, r.price.currency)],
    ["Intrinsic value", (r: InvestmentRecord) => formatCurrency(r.valuation.baseValue, r.price.currency)],
    ["Margin of safety", (r: InvestmentRecord) => formatPercent(r.valuation.marginOfSafety)],
    ["Expected CAGR", (r: InvestmentRecord) => formatPercent(r.valuation.expectedFiveYearCagr)],
    ["Dividend yield", (r: InvestmentRecord) => formatPercent(r.dividendYield)],
    ["Revenue growth", () => "Sample trend"],
    ["EPS growth", () => "Sample trend"],
    ["ROE", (r: InvestmentRecord) => formatPercent(roe(r.financials[0].netIncome, r.financials[0].shareholdersEquity).value)],
    ["ROIC", (r: InvestmentRecord) => formatPercent(roic(r.financials[0].operatingIncome, 0.3, r.financials[0].totalDebt, r.financials[0].shareholdersEquity, r.financials[0].cashAndEquivalents).value)],
    ["Debt", (r: InvestmentRecord) => formatNumber(r.financials[0].totalDebt)],
    ["Free cash flow", (r: InvestmentRecord) => formatNumber(r.financials[0].freeCashFlow)],
    ["Buffett Score", (r: InvestmentRecord) => r.quality.buffettScore],
    ["Opportunity Score", (r: InvestmentRecord) => r.quality.opportunityScore],
    ["Risk rating", (r: InvestmentRecord) => r.riskRating],
    ["Zone", (r: InvestmentRecord & { calculatedZone: Zone }) => r.calculatedZone]
  ] as const;

  return (
    <div className="space-y-5">
      <div className="surface rounded-md p-5">
        <h2 className="text-2xl font-semibold">Company Comparison</h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((index) => (
            <Select key={index} value={selected[index] ?? ""} onChange={(value) => setSelected((items) => Object.assign([...items], { [index]: value }).slice(0, 4))} options={records.map((record) => record.company.slug)} />
          ))}
        </div>
      </div>
      <div className="surface overflow-x-auto rounded-md">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-slate-900 light:bg-slate-100"><tr><th className="px-4 py-3 text-left">Metric</th>{selectedRecords.map((record) => <th className="px-4 py-3 text-right" key={record.company.id}>{record.company.ticker}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-800 light:divide-slate-200">
            {rows.map(([label, getter]) => <tr key={label}><td className="px-4 py-3">{label}</td>{selectedRecords.map((record) => <td className="px-4 py-3 text-right" key={`${label}-${record.company.id}`}>{getter(record)}</td>)}</tr>)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Portfolio({ records, portfolio, setPortfolio }: { records: Array<InvestmentRecord & { calculatedZone: Zone }>; portfolio: PortfolioPosition[]; setPortfolio: (positions: PortfolioPosition[]) => void }) {
  const rows = portfolio.map((position) => {
    const record = records.find((item) => item.company.id === position.companyId);
    const current = record?.price.price ?? 0;
    const currentValue = current * position.quantity;
    const cost = position.averageCost * position.quantity;
    return { position, record, currentValue, gain: currentValue - cost, weight: 0 };
  });
  const total = rows.reduce((sum, row) => sum + row.currentValue, 0);
  const gain = rows.reduce((sum, row) => sum + row.gain, 0);
  const expected = rows.reduce((sum, row) => sum + row.currentValue * (row.record?.valuation.expectedFiveYearCagr ?? 0), 0) / (total || 1);
  const benchmarkGap = returnVersusBenchmark(expected, savingsBenchmark).value ?? 0;

  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-4">
        <Stat label="Portfolio value" value={formatCurrency(total, "USD")} icon={BriefcaseBusiness} />
        <Stat label="Gain/loss" value={formatCurrency(gain, "USD")} icon={CircleDollarSign} />
        <Stat label="Expected CAGR" value={formatPercent(expected)} icon={Gauge} />
        <Stat label="Vs 11% benchmark" value={formatPercent(benchmarkGap)} icon={Scale} />
      </div>
      <div className="surface rounded-md p-5">
        <h2 className="text-2xl font-semibold">Portfolio Positions</h2>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead className="bg-slate-900 text-slate-400 light:bg-slate-100"><tr>{["Ticker", "Quantity", "Average cost", "Current value", "Gain/loss", "Weight", "Dividend income", "Status"].map((h) => <th className="px-4 py-3 text-right first:text-left" key={h}>{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-slate-800 light:divide-slate-200">
              {rows.map((row) => <tr key={row.position.id}><td className="px-4 py-3">{row.position.ticker}</td><td className="px-4 py-3 text-right">{row.position.quantity}</td><td className="px-4 py-3 text-right">{formatCurrency(row.position.averageCost, row.position.currency)}</td><td className="px-4 py-3 text-right">{formatCurrency(row.currentValue, row.position.currency)}</td><td className="px-4 py-3 text-right">{formatCurrency(row.gain, row.position.currency)}</td><td className="px-4 py-3 text-right">{formatPercent(total ? row.currentValue / total : 0)}</td><td className="px-4 py-3 text-right">{formatCurrency(row.currentValue * (row.record?.dividendYield ?? 0), row.position.currency)}</td><td className="px-4 py-3 text-right"><StatusPill status="sample" /></td></tr>)}
            </tbody>
          </table>
        </div>
        <PortfolioForm records={records} onAdd={(position) => setPortfolio([...portfolio, position])} />
      </div>
      <Allocation title="Sector Allocation" rows={rows.map((row) => ({ label: row.record?.company.sector ?? "Unknown", value: row.currentValue }))} />
      <Allocation title="Market Allocation" rows={rows.map((row) => ({ label: row.record?.company.market ?? "Unknown", value: row.currentValue }))} />
    </div>
  );
}

function PortfolioForm({ records, onAdd }: { records: InvestmentRecord[]; onAdd: (position: PortfolioPosition) => void }) {
  const [ticker, setTicker] = useState(records[0]?.company.id ?? "");
  const [quantity, setQuantity] = useState(1);
  const [averageCost, setAverageCost] = useState(100);
  return (
    <form className="mt-5 grid gap-3 md:grid-cols-4" onSubmit={(event) => {
      event.preventDefault();
      const record = records.find((item) => item.company.id === ticker);
      if (!record) return;
      onAdd({ id: crypto.randomUUID(), ticker: record.company.ticker, companyId: record.company.id, quantity, averageCost, currency: record.price.currency, purchaseDate: new Date().toISOString().slice(0, 10) });
    }}>
      <Select value={ticker} onChange={setTicker} options={records.map((record) => record.company.id)} />
      <input className="field" type="number" min="0.0001" step="0.0001" value={quantity} onChange={(event) => setQuantity(Number(event.target.value))} />
      <input className="field" type="number" min="0" step="0.01" value={averageCost} onChange={(event) => setAverageCost(Number(event.target.value))} />
      <button className="rounded-md bg-blue px-4 py-2 font-semibold text-white">Add position</button>
    </form>
  );
}

function Allocation({ title, rows }: { title: string; rows: Array<{ label: string; value: number }> }) {
  const grouped = rows.reduce<Record<string, number>>((acc, row) => ({ ...acc, [row.label]: (acc[row.label] ?? 0) + row.value }), {});
  const total = Object.values(grouped).reduce((sum, value) => sum + value, 0);
  return (
    <div className="surface rounded-md p-5">
      <h3 className="font-semibold">{title}</h3>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        {Object.entries(grouped).map(([label, value]) => <Band key={label} label={`${label} (${formatPercent(total ? value / total : 0)})`} value={value} currency="USD" />)}
      </div>
    </div>
  );
}

function Admin({ records, settings, setSettings }: { records: Array<InvestmentRecord & { calculatedZone: Zone }>; settings: GoldZoneSettings; setSettings: (settings: GoldZoneSettings) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState(isSupabaseConfigured ? "Sign in with a Supabase admin or analyst account." : "Supabase is not configured. Admin actions are shown in disabled preview mode.");
  const authAction = async (action: "login" | "reset" | "logout") => {
    if (!supabase) return;
    if (action === "login") {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      setMessage(error?.message ?? "Signed in. Role must be assigned in Supabase app_metadata.");
    } else if (action === "reset") {
      const { error } = await supabase.auth.resetPasswordForEmail(email);
      setMessage(error?.message ?? "Password reset email requested.");
    } else {
      await supabase.auth.signOut();
      setMessage("Signed out.");
    }
  };
  return (
    <div className="space-y-5">
      <div className="surface rounded-md p-5">
        <div className="flex items-center gap-2"><UserRound size={19} /><h2 className="text-2xl font-semibold">Admin Console</h2></div>
        <div className="mt-4 grid gap-3 md:grid-cols-[1fr_1fr_auto_auto_auto]">
          <input className="field" type="email" placeholder="admin@example.com" value={email} onChange={(event) => setEmail(event.target.value)} disabled={!isSupabaseConfigured} />
          <input className="field" type="password" placeholder="Password" value={password} onChange={(event) => setPassword(event.target.value)} disabled={!isSupabaseConfigured} />
          <button className="rounded-md bg-blue px-4 py-2 font-semibold text-white disabled:opacity-50" disabled={!isSupabaseConfigured} onClick={() => void authAction("login")}><LogIn className="mr-2 inline" size={16} />Login</button>
          <button className="rounded-md border border-slate-700 px-4 py-2 disabled:opacity-50 light:border-slate-300" disabled={!isSupabaseConfigured} onClick={() => void authAction("reset")}>Reset</button>
          <button className="rounded-md border border-slate-700 px-4 py-2 disabled:opacity-50 light:border-slate-300" disabled={!isSupabaseConfigured} onClick={() => void authAction("logout")}><LogOut className="mr-2 inline" size={16} />Logout</button>
        </div>
        <p className="mt-3 text-sm text-slate-400">{message}</p>
      </div>
      <GoldZone records={records} settings={settings} setSettings={setSettings} onSelect={() => undefined} />
      <div className="grid gap-4 lg:grid-cols-3">
        <AdminPanel title="Data Management" lines={["Add companies", "Edit company information", "Add prices", "Import financial periods", "Approve staging records"]} />
        <AdminPanel title="Valuation Workflow" lines={["Edit assumptions", "Edit qualitative scores", "View validation warnings", "Recalculate valuations", "Publish updated results"]} />
        <AdminPanel title="Agent Review" lines={["View agent runs", "Approve or reject agent-generated analysis", "AI disabled gracefully without OPENAI_API_KEY"]} />
      </div>
    </div>
  );
}

function AdminPanel({ title, lines }: { title: string; lines: string[] }) {
  return (
    <div className="surface rounded-md p-5">
      <h3 className="font-semibold">{title}</h3>
      <ul className="mt-4 space-y-3 text-sm text-slate-300 light:text-slate-700">
        {lines.map((line) => <li className="flex items-center gap-2" key={line}><CheckCircle2 size={16} className="text-gold" />{line}</li>)}
      </ul>
    </div>
  );
}

function TextPanel({ title, lines }: { title: string; lines: string[] }) {
  return (
    <div className="surface rounded-md p-5">
      <h3 className="font-semibold">{title}</h3>
      <ul className="mt-4 space-y-3 text-sm leading-6 text-slate-300 light:text-slate-700">
        {lines.map((line) => <li key={line}>{line}</li>)}
      </ul>
    </div>
  );
}

function ZonePill({ zone }: { zone: Zone }) {
  return <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${zoneClasses[zone]}`}>{zone}</span>;
}

function StatusPill({ status }: { status: "approved" | "pending" | "sample" | "stale" | "unavailable" }) {
  return <span className="mt-2 inline-flex rounded-full border border-slate-700 px-2 py-0.5 text-[11px] uppercase text-slate-400 light:border-slate-300 light:text-slate-600">{statusLabel(status)}</span>;
}

function EmptyState() {
  return (
    <div className="surface rounded-md p-8 text-center">
      <Download className="mx-auto text-gold" size={32} />
      <h2 className="mt-4 text-2xl font-semibold">No approved records yet</h2>
      <p className="mx-auto mt-3 max-w-xl text-sm text-slate-400">Add your first company in Admin, run the Supabase migration, then load approved records. The demo-data seed can be loaded from `supabase/seed.sql` for development.</p>
    </div>
  );
}

export default App;





